import { useRef, useState, type FormEvent } from "react";
import axios from "axios";
import {
  type AddressMediaFields,
  useAddressMedia,
  useCreateAddressMedia,
  useSearchAddressMedia,
} from "@/api/address-medias";
import type { AddressMedia } from "@/api.types.v2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY_ADDRESS: AddressMediaFields = {
  streetNumber: "",
  streetName: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  placeId: "",
};
const ADDRESS_FIELDS = [
  ["streetNumber", "Street number", false],
  ["streetName", "Street name", true],
  ["city", "City", true],
  ["state", "Province / state", true],
  ["country", "Country code", true],
  ["postalCode", "Postal code", true],
  ["placeId", "Place ID (optional)", false],
] as const;

function AddressFields({
  value,
  onChange,
  disabled,
  prefix,
  search = false,
}: {
  value: AddressMediaFields;
  onChange: (value: AddressMediaFields) => void;
  disabled?: boolean;
  prefix: string;
  search?: boolean;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {ADDRESS_FIELDS.filter(
        ([field]) => !search || field !== "streetNumber"
      ).map(([field, label, required]) => (
        <div key={field} className="space-y-1">
          <Label htmlFor={`${prefix}-${field}`}>
            {label}
            {required ? " *" : ""}
          </Label>
          <Input
            id={`${prefix}-${field}`}
            value={value[field] ?? ""}
            required={required}
            disabled={disabled}
            onChange={(event) =>
              onChange({ ...value, [field]: event.target.value })
            }
          />
        </div>
      ))}
    </div>
  );
}

function validAddress(value: AddressMediaFields) {
  return ADDRESS_FIELDS.every(
    ([field, , required]) => !required || !!value[field]?.trim()
  );
}

async function readImage(file: File) {
  if (
    !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
    !file.size ||
    file.size > 5 * 1024 * 1024
  ) {
    throw new Error(
      "Choose a PNG, JPEG or WebP image between 1 byte and 5 MB."
    );
  }
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error("The image could not be read. Choose it again."));
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("The image could not be read."));
    reader.readAsDataURL(file);
  });
  const bytes = atob(data.split(",")[1] ?? "");
  const matches =
    file.type === "image/png"
      ? bytes.startsWith("\x89PNG\r\n\x1a\n")
      : file.type === "image/jpeg"
        ? bytes.startsWith("\xff\xd8\xff")
        : bytes.startsWith("RIFF") && bytes.slice(8, 12) === "WEBP";
  if (!matches)
    throw new Error("The selected file does not match its image format.");
  return data;
}

export function AddressMediaDetails({ data }: { data: AddressMedia }) {
  return (
    <div className="space-y-3">
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">Record ID</dt>
          <dd className="break-all">{data.id}</dd>
        </div>
        {ADDRESS_FIELDS.map(([field, label]) => (
          <div key={field}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{data[field] || "—"}</dd>
          </div>
        ))}
        <div>
          <dt className="text-muted-foreground">Created</dt>
          <dd>{data.createdAt}</dd>
        </div>
      </dl>
      {(data.mediaItems ?? []).map((item) => (
        <div key={item.id} className="space-y-1 rounded border p-2">
          <img
            src={item.url}
            alt={item.description || "Address image"}
            className="max-h-64 rounded object-contain"
          />
          <p className="text-sm">{item.description}</p>
          <p className="break-all text-xs text-muted-foreground">
            Media ID: {item.id}
          </p>
        </div>
      ))}
      {!data.mediaItems?.length && (
        <p>No media items are attached to this record.</p>
      )}
    </div>
  );
}

export function AddressMediaDetailDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const record = useAddressMedia(id ?? "");
  return (
    <Dialog
      open={!!id}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Address media details</DialogTitle>
          <DialogDescription>
            Current address and media saved for this record.
          </DialogDescription>
        </DialogHeader>
        {record.isFetching ? (
          <p role="status">Loading address media…</p>
        ) : record.error ? (
          <div role="alert">
            <p>Address media could not be loaded.</p>
            <Button variant="outline" onClick={() => void record.refetch()}>
              Try again
            </Button>
          </div>
        ) : record.data ? (
          <AddressMediaDetails data={record.data} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function CreateAddressMediaDialog({
  open,
  onOpenChange,
  onFind,
  onRead,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFind: (fields: AddressMediaFields) => void;
  onRead: (id: string) => void;
}) {
  const [fields, setFields] = useState(EMPTY_ADDRESS);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [created, setCreated] = useState<AddressMedia | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const create = useCreateAddressMedia();
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (lock.current || outcomeUnknown || created) return;
    if (!validAddress(fields) || !file) {
      setError("Complete the required address fields and choose one image.");
      return;
    }
    lock.current = true;
    setBusy(true);
    setError(null);
    let requestStarted = false;
    try {
      const base64Data = await readImage(file);
      requestStarted = true;
      const result = await create.mutateAsync({
        ...fields,
        mediaItems: [
          {
            base64Data,
            type: "IMAGE",
            mimeType: file.type,
            ...(description ? { description } : {}),
          },
        ],
      });
      setCreated(result);
    } catch (caught) {
      const rejected =
        axios.isAxiosError(caught) &&
        [400, 401, 403].includes(caught.response?.status ?? 0);
      if (requestStarted && !rejected) {
        setOutcomeUnknown(true);
        setError(
          "Creation could not be confirmed. The address or image may already have been saved. Find the submitted address and check its record before any new creation attempt."
        );
      } else {
        setError(
          requestStarted
            ? "Address media was rejected. Check the address fields and your access before trying again."
            : caught instanceof Error
              ? caught.message
              : "The image could not be read."
        );
      }
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const close = (next: boolean) => {
    if (busy) return;
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        onInteractOutside={(event) => {
          if (busy) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Address Media</DialogTitle>
          <DialogDescription>
            Add one address image. Address formatting is checked and
            standardized when saved.
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-3">
            <p role="status">Address media created. Record ID: {created.id}</p>
            <AddressMediaDetails data={created} />
            <Button
              onClick={() => {
                const id = created.id;
                close(false);
                onRead(id);
              }}
            >
              Read saved record
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFields(EMPTY_ADDRESS);
                setFile(null);
                setDescription("");
                setCreated(null);
                setError(null);
              }}
            >
              Create another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <AddressFields
              value={fields}
              onChange={setFields}
              disabled={busy || outcomeUnknown}
              prefix="create-media"
            />
            <div className="space-y-1">
              <Label htmlFor="create-media-image">Image *</Label>
              <Input
                id="create-media-image"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={busy || outcomeUnknown}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setError(null);
                }}
              />
              <p className="text-xs text-muted-foreground">
                PNG, JPEG or WebP, up to 5 MB. Use an image you are permitted to
                share.
              </p>
              {file && (
                <p className="break-all text-xs text-muted-foreground">
                  Selected image: {file.name} ({file.type}, {file.size} bytes)
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="create-media-description">
                Description (optional)
              </Label>
              <Input
                id="create-media-description"
                value={description}
                disabled={busy || outcomeUnknown}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {outcomeUnknown ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  onFind(fields);
                }}
              >
                Find submitted address
              </Button>
            ) : (
              <Button type="submit" disabled={busy}>
                {busy ? "Saving…" : "Create address media"}
              </Button>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FindAddressMediaDialog({
  open,
  initialFields,
  onClose,
  onRead,
}: {
  open: boolean;
  initialFields: AddressMediaFields;
  onClose: () => void;
  onRead: (id: string) => void;
}) {
  const [fields, setFields] = useState(initialFields);
  const [submitted, setSubmitted] = useState<AddressMediaFields | null>(null);
  const [invalid, setInvalid] = useState(false);
  const search = useSearchAddressMedia(submitted);
  const found = search.data;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Find Address Media</DialogTitle>
          <DialogDescription>
            Search by street name, city, province/state, country and postal
            code, optionally Place ID. Check the returned street number and
            image; a match alone does not confirm an earlier upload completed.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!validAddress(fields)) {
              setInvalid(true);
              return;
            }
            setInvalid(false);
            if (
              submitted &&
              JSON.stringify(fields) === JSON.stringify(submitted)
            )
              void search.refetch();
            else setSubmitted({ ...fields });
          }}
        >
          <AddressFields
            value={fields}
            onChange={setFields}
            prefix="find-media"
            search
          />
          {invalid && <p role="alert">Complete the required address fields.</p>}
          <Button type="submit" disabled={search.isFetching}>
            {search.isFetching ? "Searching…" : "Find address"}
          </Button>
        </form>
        {search.error ? (
          <p role="alert">
            Address search failed. Check the address and your access, then try
            again.
          </p>
        ) : search.isFetching ? (
          <p role="status">Searching address media…</p>
        ) : found ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Result for the last submitted search.
            </p>
            <AddressMediaDetails data={found} />
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                onRead(found.id);
              }}
            >
              Read saved record
            </Button>
          </div>
        ) : submitted && !search.isLoading ? (
          <p>
            No matching address media found. An incomplete upload may still need
            reconciliation; do not automatically recreate it.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export { EMPTY_ADDRESS };
